/**
 * NumberWidget component.
 * @module components/manage/Widgets/NumberWidget
 *
 * added aria- attributes
 */
// Customização: NumberWidget customizado com validação de entrada numérica e limite de comprimento.
// Ver: https://github.com/collective/volto-form-block/issues/155
import React, { Component } from 'react';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Input } from 'semantic-ui-react';

// Teclas sempre permitidas, independente de formarem um número válido
const ALLOWED_CONTROL_KEYS = [
  'Backspace',
  'Delete',
  'Tab',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'Enter',
  'Escape',
];

// Caracteres aceitos por input[type=number] no Chrome: dígitos, sinal e notação científica
const ALLOWED_CHAR_REGEX = /^[0-9eE+\-.]$/;

/**
 * The simple text widget.
 *
 * It is the default fallback widget, so if no other widget is found based on
 * passed field properties, it will be used.
 */
class NumberWidget extends Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    required: PropTypes.bool,
    error: PropTypes.arrayOf(PropTypes.string),
    value: PropTypes.string,
    focus: PropTypes.bool,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onClick: PropTypes.func,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func,
    icon: PropTypes.shape({
      xmlns: PropTypes.string,
      viewBox: PropTypes.string,
      content: PropTypes.string,
    }),
    iconAction: PropTypes.func,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    size: PropTypes.number,
    wrapped: PropTypes.bool,
    placeholder: PropTypes.string,
  };

  static defaultProps = {
    description: null,
    required: false,
    error: [],
    value: null,
    onChange: () => {},
    onBlur: () => {},
    onClick: () => {},
    onEdit: null,
    onDelete: null,
    focus: false,
    icon: null,
    iconAction: null,
    minLength: null,
    maxLength: null,
    size: null,
  };

  componentDidMount() {
    if (this.props.focus) {
      this.node.focus();
    }
  }

  /**
   * Bloqueia teclas que não fazem parte de um número válido,
   * replicando o comportamento nativo do Chrome em input[type=number].
   * @method onKeyDown
   * @param {Object} e Evento de teclado
   * @returns {undefined}
   */
  onKeyDown = (e) => {
    // Permite combinações com Ctrl/Cmd (copiar, colar, selecionar tudo, etc.)
    if (e.ctrlKey || e.metaKey) {
      return;
    }

    if (ALLOWED_CONTROL_KEYS.includes(e.key)) {
      return;
    }

    // e.key para teclas de um único caractere ('1', 'e', '-', etc.)
    if (e.key.length === 1 && !ALLOWED_CHAR_REGEX.test(e.key)) {
      e.preventDefault();
    }
  };

  /**
   * Sanitiza conteúdo colado, removendo caracteres não numéricos.
   * @method onPaste
   * @param {Object} e Evento de paste
   * @returns {undefined}
   */
  onPaste = (e) => {
    const pasted = e.clipboardData.getData('Text');
    if (!/^[0-9eE+\-.]*$/.test(pasted)) {
      e.preventDefault();
    }
  };

  render() {
    const {
      id,
      value,
      onChange,
      onBlur,
      onClick,
      icon,
      iconAction,
      minLength,
      maxLength,
      size,
      placeholder,
      error,
      required,
      invalid,
    } = this.props;

    let attributes = {};
    if (required) {
      attributes.required = true;
      attributes['aria-required'] = true;
    }

    const isInvalid = invalid === true || invalid === 'true';
    if (error?.length > 0 || isInvalid) {
      attributes['aria-invalid'] = true;
    }

    return (
      <FormFieldWrapper {...this.props} className="text">
        <Input
          id={`field-${id}`}
          name={id}
          value={value || ''}
          disabled={this.props.isDisabled}
          icon={icon || null}
          placeholder={placeholder}
          onChange={({ target }) => {
            let val = target.value === '' ? undefined : target.value;
            if (
              val !== undefined &&
              maxLength !== null &&
              maxLength !== undefined &&
              Number(maxLength) > 0 &&
              String(val).length > Number(maxLength)
            ) {
              val = String(val).slice(0, Number(maxLength));
              // Atualiza o valor do input para refletir o valor truncado
              if (
                this.node &&
                this.node.inputRef &&
                this.node.inputRef.current
              ) {
                try {
                  this.node.inputRef.current.value = val;
                } catch (e) {}
              }
            }
            onChange(id, val === '' ? undefined : val);
          }}
          onKeyDown={this.onKeyDown}
          onPaste={this.onPaste}
          {...attributes}
          ref={(node) => {
            this.node = node;
          }}
          onBlur={({ target }) =>
            onBlur(id, target.value === '' ? undefined : target.value)
          }
          onClick={() => onClick()}
          minLength={minLength || null}
          maxLength={maxLength || null}
          style={{ width: size ? `${size}ch` : undefined }}
          type="number"
        />
        {icon && iconAction && (
          <button className={`field-${id}-action-button`} onClick={iconAction}>
            <Icon name={icon} size="18px" />
          </button>
        )}
      </FormFieldWrapper>
    );
  }
}

export default injectIntl(NumberWidget);
